import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import * as moment from "moment";
import { Moment } from "moment";
import { CalendarList, PaginationOptions, OrderedCalendar } from "burde";
import { getOrderedCalendarsWithPagination } from "calendar/service";
import { AdminHeader } from "../admin-header";
import * as styles from "./list.css";
import { ApplicationState } from "store";
import { getResource } from "resource/store";
import { useDebounce } from "hooks/use-debounce";
import { CalendarRow } from "./calendar-row";
import { StyledPagination } from "common/styled-pagination";

interface OptionType {
    value: string;
    label: string;
}

const dropDownOptions: OptionType[] = [
    { value: "10", label: "10" },
    { value: "30", label: "30" },
    { value: "60", label: "60" },
    { value: "100", label: "100" },
];

interface Translations {
    Admin_Order_Search: string;
    Admin_Order_Id: string;
    Admin_Order_User: string;
    Admin_Order_OrderDate: string;
    Admin_Order_Layout: string;
    Admin_Order_Culture: string;
    Admin_Order_Status: string;
    Admin_Order_Download: string;
    Admin_Order_Orders: string;
    Loading: string;
}

const customSelectStyles = {
    control: (base: {}) => ({
        ...base,
        height: "28px",
        "min-height": "28px",
        marginTop: "13px",
        width: "75px",
    }),
    valueContainer: (provided: {}) => ({
        ...provided,
        height: "28px",
        padding: "0 6px",
        width: "75px",
    }),
    input: (provided: {}) => ({
        ...provided,
        margin: "0px",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    indicatorsContainer: (provided: {}) => ({
        ...provided,
        height: "28px",
    }),
    menu: (provided: {}) => ({ ...provided, width: "75px" }),
};

interface DateSearch {
    fromDate?: Moment | null;
    toDate?: Moment | null;
}

const initialPaginationOptions: PaginationOptions & DateSearch = {
    searchText: "",
    fromDate: null,
    toDate: null,
    pageNumber: 1,
    totalNumber: 0,
    numberOfPages: 0,
};

export const OrdersList = (
    props: CalendarList & Translations,
): React.ReactElement => {
    const [calendars, setCalendars] = useState<OrderedCalendar[]>([]);
    const [pagination, setPagination] = useState<
        PaginationOptions & DateSearch
    >(initialPaginationOptions);
    const [loading, setLoading] = useState(false);
    const [resultsPerPage, setResultsPerPage] = useState(30);

    const {
        numberOfPages,
        searchText,
        totalNumber,
        fromDate,
        toDate,
        pageNumber,
    } = pagination;

    const debouncedSearchValue = useDebounce(searchText, 300);

    useEffect(() => {
        getCalendars();
    }, [fromDate, toDate, pageNumber, resultsPerPage, debouncedSearchValue]);

    const getCalendars = async () => {
        setLoading(true);
        const fromDateString = dateFormat(fromDate);
        const toDateString = dateFormat(toDate);

        const searchResult = await getOrderedCalendarsWithPagination(
            fromDateString,
            toDateString,
            pageNumber,
            searchText || "",
            resultsPerPage,
        );

        if (searchResult.success) {
            const { pageNumber, totalNumber, pages, calendars } = searchResult;

            const sortedCalendars = calendars.sort((a, b) => {
                return b.calendarId - a.calendarId;
            });

            setCalendars(sortedCalendars);
            setPagination({
                ...pagination,
                pageNumber,
                totalNumber,
                numberOfPages: pages,
            });
            setLoading(false);
        }
    };

    const dateFormat = (date: Moment | null | undefined) =>
        date ? moment(date).format("YYYY-MM-DD") : "";

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPagination({ ...pagination, [name]: value });
    };

    const handleResultsPerPage = (e: any) => setResultsPerPage(e.value);

    return (
        <div className={"container mt-4"}>
            <AdminHeader title={props.Admin_Order_Orders} />

            <div className={styles.inputContainer}>
                <input
                    placeholder={props.Admin_Order_Search}
                    className={styles.searchField}
                    type="text"
                    value={searchText}
                    name="searchText"
                    onChange={handleChange}
                />
                <input
                    type="date"
                    className={styles.dateField}
                    name="fromDate"
                    onChange={handleChange}
                />
                <input
                    type="date"
                    className={styles.dateField}
                    name="toDate"
                    onChange={handleChange}
                />
            </div>

            {loading ? (
                <div style={{ textAlign: "center" }}>
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p>{props.Loading}</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th scope="col">{props.Admin_Order_Id}</th>
                                <th scope="col">{props.Admin_Order_User}</th>
                                <th scope="col">
                                    {props.Admin_Order_OrderDate}
                                </th>
                                <th scope="col">{props.Admin_Order_Layout}</th>
                                <th scope="col">{props.Admin_Order_Culture}</th>
                                <th scope="col">{props.Admin_Order_Status}</th>
                                <th
                                    className={"text-right"}
                                    scope="col"
                                    colSpan={2}>
                                    {calendars.length}/{totalNumber}
                                </th>
                            </tr>
                        </thead>
                        <CalendarRow
                            calendars={calendars}
                            download={props.Admin_Order_Download}
                        />
                    </table>
                </div>
            )}

            <div
                className="container"
                style={loading ? { pointerEvents: "none", opacity: 0.4 } : {}}>
                <div className="row">
                    <div
                        className="col-sm-8 ml-auto"
                        style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Select
                            options={dropDownOptions}
                            defaultValue={dropDownOptions[1]}
                            isSearchable={false}
                            onChange={handleResultsPerPage}
                            styles={customSelectStyles}
                            isDisabled={calendars.length < 10}
                        />
                    </div>
                    <div className="col-sm-4">
                        <StyledPagination
                            numberOfPages={numberOfPages}
                            setPagination={setPagination}
                            paginationOptions={pagination}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    Admin_Order_Search: getResource(state, {
        key: "Admin_Order_Search",
    }),
    Admin_Order_Id: getResource(state, {
        key: "Admin_Order_Id",
    }),
    Admin_Order_User: getResource(state, {
        key: "Admin_Order_User",
    }),
    Admin_Order_OrderDate: getResource(state, {
        key: "Admin_Order_OrderDate",
    }),
    Admin_Order_Layout: getResource(state, {
        key: "Admin_Order_Layout",
    }),
    Admin_Order_Culture: getResource(state, {
        key: "Admin_Order_Culture",
    }),
    Admin_Order_Status: getResource(state, {
        key: "Admin_Order_Status",
    }),
    Admin_Order_Download: getResource(state, {
        key: "Admin_Order_Download",
    }),
    Admin_Order_Orders: getResource(state, {
        key: "Admin_Order_Orders",
    }),
    Loading: getResource(state, {
        key: "Loading",
    }),
});

export const ConnectedOrdersList = connect(mapStateToProps)(OrdersList) as any;
